import React, { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "@clerk/clerk-react";
import ImageFormatToolbar from "../../../../components/shared/imageToolbar/ImageFormatToolbar";
import FeaturePaywallModal from "../../../../components/popups/paywall/FeaturePaywallModal";
import { ensureBackgroundRemoved } from "../../../../utils/ensureBackgroundRemoved";
import { canAccessPaywallFeature } from "../../../../data/paywallFeatures";
import { DEFAULT_WATERMARK_SETTINGS } from "./watermarkSettings";
import {
  getWatermarkImageFormatTools,
  imageFormatPatchToWatermark,
  isWatermarkLogoToolbar,
  watermarkToImageFormat,
} from "../Editor/watermarkImageFormatAdapter";
import "./WatermarkTextFormatToolbar.css";

/**
 * Host wrapper — mounts generic ImageFormatToolbar for logo watermarks.
 * BG Remover is gated by FeaturePaywallModal (Maker / PDF / Word / editor).
 */
const WatermarkImageFormatToolbar = ({
  watermark = null,
  onChange = () => {},
  visible = true,
  disabled = false,
  className = "watermark-text-format-toolbar",
  keepEnabled = false,
  /** watermark_maker | pdf_watermarker | word_watermarker | editor_watermark */
  toolKey = "watermark_maker",
  /** Optional tool-id list; defaults to watermark image tool set. */
  tools: toolsOverride = null,
}) => {
  const { getToken } = useAuth();
  const [bgRemoveBusy, setBgRemoveBusy] = useState(false);
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [bgRemoveError, setBgRemoveError] = useState(null);
  const cutoutUrlRef = useRef(null);

  const settings = useMemo(
    () => ({
      ...DEFAULT_WATERMARK_SETTINGS,
      ...(watermark || {}),
    }),
    [watermark]
  );

  const settingsRef = useRef(settings);
  settingsRef.current = settings;

  const imageValue = useMemo(
    () => watermarkToImageFormat(settings),
    [settings]
  );
  const tools = useMemo(
    () =>
      Array.isArray(toolsOverride)
        ? toolsOverride
        : getWatermarkImageFormatTools(),
    [toolsOverride]
  );

  useEffect(() => {
    setBgRemoveError(null);
  }, [settings.logoUrl]);

  const handleChange = (next) => {
    const base = settingsRef.current;
    const current = watermarkToImageFormat(base);
    const patch = {};
    for (const [key, val] of Object.entries(next)) {
      if (current[key] !== val) patch[key] = val;
    }
    if (Object.keys(patch).length === 0) return;
    const updated = imageFormatPatchToWatermark(patch, base);
    onChange(keepEnabled ? { ...updated, enabled: true } : updated);
  };

  const applyCutoutLogo = (url, cutoutCreationId = null) => {
    const apply = (nw, nh) => {
      const prev = settingsRef.current;
      const next = {
        ...prev,
        logoUrl: url,
        logoOriginalUrl: prev.logoOriginalUrl || prev.logoUrl,
        logoNaturalWidth: nw,
        logoNaturalHeight: nh,
        logoBgRemoved: true,
        logoCreationId: cutoutCreationId ?? prev.logoCreationId,
        logoCropAspect: "custom",
        logoCropRect: null,
        logoCropFrame: null,
        logoCropImageFrame: null,
        logoCropActive: false,
        logoCropSessionStartRect: null,
        logoCropSessionStartImageFrame: null,
        logoCropInset: 0,
      };
      onChange(keepEnabled ? { ...next, enabled: true } : next);
    };
    const img = new Image();
    if (
      typeof url === "string" &&
      /^https?:\/\//i.test(url) &&
      !url.startsWith("blob:")
    ) {
      img.crossOrigin = "anonymous";
    }
    img.onload = () => {
      apply(img.naturalWidth || img.width || 0, img.naturalHeight || img.height || 0);
    };
    img.onerror = () => apply(0, 0);
    img.src = url;
  };

  const runBgRemove = async () => {
    const base = settingsRef.current;
    if (bgRemoveBusy || base.logoBgRemoved || !base.logoUrl) return;
    setBgRemoveBusy(true);
    setBgRemoveError(null);
    try {
      const token = await getToken();
      if (!token) throw new Error("Sign in to remove the logo background.");
      const { cutoutUrl, creationId } = await ensureBackgroundRemoved({
        token,
        imageFile: base.logoFile instanceof File ? base.logoFile : null,
        imageUrl: base.logoOriginalUrl || base.logoUrl,
        rootCreationId: base.logoRootCreationId,
        creationId: base.logoCreationId,
      });
      if (!cutoutUrl) throw new Error("Background removal returned no image.");

      if (
        cutoutUrlRef.current?.startsWith("blob:") &&
        cutoutUrlRef.current !== cutoutUrl
      ) {
        URL.revokeObjectURL(cutoutUrlRef.current);
      }
      cutoutUrlRef.current = cutoutUrl.startsWith("blob:") ? cutoutUrl : null;
      applyCutoutLogo(cutoutUrl, creationId);
    } catch (err) {
      console.error("[WatermarkImageFormatToolbar] BG remove failed:", err);
      setBgRemoveError(
        err?.message || "Could not remove the logo background. Please try again."
      );
    } finally {
      setBgRemoveBusy(false);
    }
  };

  const handleBgRemove = () => {
    const base = settingsRef.current;
    if (bgRemoveBusy || base.logoBgRemoved || !base.logoUrl) return;
    if (!canAccessPaywallFeature("logo_bg_remove")) {
      setPaywallOpen(true);
      return;
    }
    void runBgRemove();
  };

  if (!visible || !isWatermarkLogoToolbar(settings)) return null;

  return (
    <div className={className} aria-live="polite">
      <ImageFormatToolbar
        value={imageValue}
        onChange={handleChange}
        tools={tools}
        disabled={disabled}
        onBgRemove={handleBgRemove}
        bgRemoveBusy={bgRemoveBusy}
        bgRemoveDone={Boolean(settings.logoBgRemoved)}
        sizeConfig={{
          min: 0,
          max: 24,
          formatDisplay: (v) => String(Math.round(v * 10)),
          parseDisplay: (display) => Number(display) / 10,
        }}
      />
      <FeaturePaywallModal
        open={paywallOpen}
        featureKey="logo_bg_remove"
        toolKey={toolKey}
        onClose={() => setPaywallOpen(false)}
      />
      {bgRemoveError ? (
        <p className="watermark-image-format-toolbar__error" role="alert">
          {bgRemoveError}
        </p>
      ) : null}
    </div>
  );
};

export default WatermarkImageFormatToolbar;
